import { useApolloClient } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMarkChatAsRead } from '../../../hooks';
import { GET_CHATS_BY_FILTERS, type ChatAppType } from '../../../queries/chats';
import { browserNotificationManager } from '../../../services/browser-notification-manager';
import { isTabAndWindowVisible, shouldShowBrowserNotification } from '../lib/notification-decision';
import { buildNotificationText, normalizeChatUuid, updateChatsListAfterStream } from '../lib/notification-helpers';

interface StreamRegistration {
  chatUuid: string;
  isChatVisible: boolean;
  isStandaloneMode: boolean;
}

export interface ChatNotificationController {
  // State
  hasNotifications: boolean;
  browserNotificationsEnabled: boolean;
  browserNotificationPermission: NotificationPermission;
  browserNotificationsSupported: boolean;

  // Actions
  setHasNotifications: (has: boolean) => void;
  enableBrowserNotifications(): Promise<boolean>;
  disableBrowserNotifications(): void;
  clearNotifications(): void;

  // Stream lifecycle
  registerStream(chatUuid: string, isChatVisible: boolean, isStandaloneMode: boolean): void;
  unregisterStream(chatUuid: string): void;
  onStreamFinish(params: {
    chatUuid: string;
    isChatVisible?: boolean;
    isStandaloneMode?: boolean;
    currentChatUuid?: string;
  }): Promise<void>;
  onOrphanedStreamFinish(params: { chatUuid: string; isStandaloneMode?: boolean }): Promise<void>;
}

export function useChatNotificationController({
  userUuid,
  currentChatUuid,
  appType,
}: {
  userUuid: string;
  currentChatUuid?: string;
  appType?: ChatAppType;
}): ChatNotificationController {
  const apolloClient = useApolloClient();
  const isNovaNotificationsEnabled = useFeatureFlag('nova-notifications', false);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(false);
  const isInitialized = useRef(false);
  const streamRegistrations = useRef<Map<string, StreamRegistration>>(new Map());
  const markChatAsRead = useMarkChatAsRead(userUuid, appType);

  // Query chats to check for unread messages
  const { data } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType,
    },
    skip: isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};

  // Initialize browser notification state
  useEffect(() => {
    if (userUuid && !isInitialized.current) {
      isInitialized.current = true;
      setBrowserNotificationsEnabled(browserNotificationManager.isEnabled());
      browserNotificationManager.setFeatureFlagEnabled(isNovaNotificationsEnabled);
    }
  }, [userUuid, isNovaNotificationsEnabled]);

  // Check for unread messages when chats data changes
  useEffect(() => {
    const hasUnreadMessages = chats.some((chat) => chat.hasUnreadMessages);
    setHasNotifications(hasUnreadMessages);
  }, [chats]);

  // Browser notification actions
  const enableBrowserNotifications = useCallback(async (): Promise<boolean> => {
    try {
      const permission = await browserNotificationManager.requestPermission();
      if (permission === 'granted') {
        browserNotificationManager.enable();
        setBrowserNotificationsEnabled(true);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, []);

  const disableBrowserNotifications = useCallback(() => {
    browserNotificationManager.disable();
    setBrowserNotificationsEnabled(false);
  }, []);

  const clearNotifications = useCallback(() => {
    browserNotificationManager.clearNotifications();
  }, []);

  // Stream lifecycle
  const registerStream = useCallback((chatUuid: string, isChatVisible: boolean, isStandaloneMode: boolean) => {
    streamRegistrations.current.set(chatUuid, {
      chatUuid,
      isChatVisible,
      isStandaloneMode,
    });

    browserLogger.debug('[Notification Controller] Registered stream', {
      chatUuid,
      isChatVisible,
      isStandaloneMode,
      activeStreams: streamRegistrations.current.size,
    });
  }, []);

  const unregisterStream = useCallback((chatUuid: string) => {
    const existed = streamRegistrations.current.has(chatUuid);
    streamRegistrations.current.delete(chatUuid);

    if (existed) {
      browserLogger.debug('[Notification Controller] Unregistered stream', {
        chatUuid,
        activeStreams: streamRegistrations.current.size,
      });
    }
  }, []);

  const onStreamFinish = useCallback(
    async ({
      chatUuid,
      isChatVisible: passedIsChatVisible,
      isStandaloneMode: passedIsStandaloneMode,
      currentChatUuid: passedCurrentChatUuid,
    }: {
      chatUuid: string;
      isChatVisible?: boolean;
      isStandaloneMode?: boolean;
      currentChatUuid?: string;
    }) => {
      const registration = streamRegistrations.current.get(chatUuid);
      if (!registration) {
        browserLogger.debug('[Notification Controller] Stream finished but no registration found', { chatUuid });
        return;
      }

      // Use stored values from registration time, with fallback to passed values
      const effectiveIsChatVisible = passedIsChatVisible ?? registration.isChatVisible;
      const effectiveIsStandaloneMode = passedIsStandaloneMode ?? registration.isStandaloneMode;
      const effectiveCurrentChatUuid = passedCurrentChatUuid ?? currentChatUuid;
      const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);
      const isCurrentChat =
        effectiveCurrentChatUuid !== undefined && normalizeChatUuid(effectiveCurrentChatUuid) === chatUuidWithoutPrefix;

      // Update Apollo cache
      updateChatsListAfterStream({
        apolloClient,
        chatUuid,
        userUuid,
        appType,
        hasUnreadMessages: true,
      });

      // Mark chat as read if it's the current chat
      if (isCurrentChat && effectiveIsChatVisible) {
        markChatAsRead(chatUuidWithoutPrefix);
      }

      // Build notification text
      const notificationText = await buildNotificationText({
        apolloClient,
        chatUuid,
      });

      const tabAndWindowVisible = isTabAndWindowVisible();

      // Determine if we should show browser notification
      const shouldShow = shouldShowBrowserNotification({
        isChatVisible: effectiveIsChatVisible,
        isStandaloneMode: effectiveIsStandaloneMode,
        isCurrentChat,
        isOrphanedStream: false,
      });

      // Show browser notification if enabled and conditions are met
      if ((browserNotificationManager.isEnabled() && shouldShow) || !tabAndWindowVisible) {
        // Calculate forceShow: show notification even if tab/window is visible when:
        // - Chat is minimized (user can't see it)
        // - In standalone mode (user might have switched chats)
        const forceShow = !effectiveIsChatVisible || effectiveIsStandaloneMode;

        browserLogger.debug('[Notification Controller] Showing browser notification', {
          chatUuid,
          notificationText,
          forceShow,
          tabAndWindowVisible,
          effectiveIsChatVisible,
          effectiveIsStandaloneMode,
        });

        browserNotificationManager.showChatNotification(notificationText, chatUuid);
      } else {
        browserLogger.debug('[Notification Controller] Skipping browser notification', {
          chatUuid,
          shouldShow,
          isEnabled: browserNotificationManager.isEnabled(),
          effectiveIsChatVisible,
          effectiveIsStandaloneMode,
          isCurrentChat,
        });
      }

      // Update notification badge state
      setHasNotifications(true);

      // Clean up registration
      unregisterStream(chatUuid);
    },
    [apolloClient, userUuid, currentChatUuid, markChatAsRead, unregisterStream],
  );

  const onOrphanedStreamFinish = useCallback(
    async ({ chatUuid }: { chatUuid: string }) => {
      browserLogger.info('[Notification Controller] Handling orphaned stream finish', { chatUuid });

      updateChatsListAfterStream({
        apolloClient,
        chatUuid,
        userUuid,
        appType,
        hasUnreadMessages: true,
      });

      const notificationText = await buildNotificationText({
        apolloClient,
        chatUuid,
      });

      if (browserNotificationManager.isEnabled()) {
        browserLogger.debug('[Notification Controller] Showing browser notification for orphaned stream', {
          chatUuid,
          notificationText,
        });

        browserNotificationManager.showChatNotification(notificationText, chatUuid);
      }

      setHasNotifications(true);
    },
    [apolloClient, userUuid],
  );

  return {
    hasNotifications,
    browserNotificationsEnabled,
    browserNotificationPermission: browserNotificationManager.getPermission(),
    browserNotificationsSupported: browserNotificationManager.isSupported(),
    setHasNotifications,
    enableBrowserNotifications,
    disableBrowserNotifications,
    clearNotifications,
    registerStream,
    unregisterStream,
    onStreamFinish,
    onOrphanedStreamFinish,
  };
}
